import type { ServiceCatalog, ServiceEntry } from './types';

/**
 * Parse the VITE_SERVICES env into ServiceEntry[], ordered by catalog.order.
 *
 * Input format: "id=url,id=url,..."
 *   monitoring=/monitoring,admin=https://admin.example.com,analytics=/analytics
 *
 * Rules:
 *  - Unknown IDs (not in catalog) are dropped with a console.warn.
 *  - Entries with empty URLs are dropped (soft-disable).
 *  - Duplicate IDs: last wins.
 *  - Whitespace trimmed around id/url.
 */
export function parseServicesEnv(
  raw: string | undefined | null,
  catalog: ServiceCatalog,
): ServiceEntry[] {
  if (!raw || raw.trim() === '') return [];

  const pairs = raw.split(',').map((s) => s.trim()).filter(Boolean);
  const seen = new Map<string, ServiceEntry>();

  for (const pair of pairs) {
    const eq = pair.indexOf('=');
    if (eq < 0) {
      console.warn(`[services] skipping malformed entry (missing =): "${pair}"`);
      continue;
    }
    const id = pair.slice(0, eq).trim();
    const url = pair.slice(eq + 1).trim();
    if (!id || !url) continue;

    const meta = catalog[id];
    if (!meta) {
      console.warn(`[services] skipping "${id}" — not declared in catalog`);
      continue;
    }

    seen.set(id, {
      id,
      url,
      name: meta.name,
      icon: meta.icon,
      order: meta.order ?? 1000,
    });
  }

  return Array.from(seen.values()).sort(
    (a, b) => (a.order ?? 1000) - (b.order ?? 1000),
  );
}

/** Mark the service whose URL matches the current location prefix. */
export function findActiveServiceId(
  services: ServiceEntry[],
  currentPath: string,
): string | null {
  // Prefer longest matching prefix (so "/admin/settings" matches "/admin" not "/")
  let best: { id: string; len: number } | null = null;
  for (const s of services) {
    if (!s.url.startsWith('/')) continue; // absolute URLs aren't "active" here
    const prefix = s.url.endsWith('/') ? s.url : s.url + '/';
    if (currentPath === s.url || currentPath.startsWith(prefix)) {
      if (!best || s.url.length > best.len) {
        best = { id: s.id, len: s.url.length };
      }
    }
  }
  return best?.id ?? null;
}
